import React, { useState, useEffect } from 'react';
import MapPicker from '../components/MapPicker';
import './AddressForm.css';

export default function AddressForm({ 
  isOpen, 
  onClose, 
  onSubmit, 
  editingAddress,
  userPhone 
}) {
  const [step, setStep] = useState('map'); // 'map' or 'form'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: userPhone || '',
    street: '',
    floor: '',
    area: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    type: 'home',
    coordinates: null
  });

  useEffect(() => {
    if (isOpen) {
      if (editingAddress) {
        setFormData({
          name: editingAddress.name || '',
          phone: editingAddress.phone || userPhone || '',
          street: editingAddress.street || '',
          floor: editingAddress.floor || '',
          area: editingAddress.area || editingAddress.city || '',
          city: editingAddress.city || '',
          state: editingAddress.state || '',
          pincode: editingAddress.pincode || '',
          landmark: editingAddress.landmark || '',
          type: editingAddress.type || 'home',
          coordinates: editingAddress.coordinates || null
        });
        setStep('form');
      } else {
        resetForm();
        setStep('map');
      }
    }
  }, [isOpen, editingAddress, userPhone]);

  const resetForm = () => {
    setFormData({
      name: '',
      phone: userPhone || '',
      street: '',
      floor: '',
      area: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      type: 'home',
      coordinates: null
    });
    setSelectedLocation(null);
    setSearchQuery('');
    setStep('map');
  };

  const handleLocationSelect = (locationData) => {
    setSelectedLocation(locationData);
    setFormData(prev => ({
      ...prev,
      street: locationData.street || prev.street,
      area: locationData.area || prev.area,
      city: locationData.city || prev.city,
      state: locationData.state || prev.state,
      pincode: locationData.pincode || prev.pincode,
      coordinates: {
        lat: locationData.lat,
        lng: locationData.lng
      }
    }));
    
    // Auto-fill city and state if available from map
    if (locationData.city && !formData.city) {
      setFormData(prev => ({ ...prev, city: locationData.city }));
    }
    if (locationData.state && !formData.state) {
      setFormData(prev => ({ ...prev, state: locationData.state }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type
    }));
  };

  const handleConfirmLocation = () => {
    if (!selectedLocation) {
      alert('Please select a location on the map');
      return;
    }
    setStep('form');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.street || !formData.area || !formData.city || !formData.state || !formData.pincode) {
      alert('Please fill all required fields');
      return;
    }
    
    // Map form data to backend schema
    const addressData = {
      name: formData.name,
      phone: formData.phone,
      street: formData.street,
      floor: formData.floor,
      area: formData.area,
      city: formData.city,
      state: formData.state,
      pincode: formData.pincode,
      landmark: formData.landmark,
      type: formData.type,
      coordinates: formData.coordinates
    };
    
    onSubmit(addressData);
  };

  const getAddressTypeIcon = (type) => {
    const icons = {
      home: '🏠',
      work: '🏢',
      hotel: '🏨',
      other: '📍'
    };
    return icons[type] || '📍';
  };

  if (!isOpen) return null;

  return (
    <div className="address-form-overlay" onClick={onClose}>
      <div className="address-form-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>

        {/* Desktop/Tablet Layout: Map on left, Form on right */}
        <div className="address-form-desktop">
          <div className="map-section">
            <div className="map-header">
              <h2>Confirm map pin location</h2>
            </div>
            <MapPicker
              onLocationSelect={handleLocationSelect}
              initialLocation={formData.coordinates || { lat: 26.7588, lng: 83.3697 }}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              pincode={formData.pincode}
            />
            <div className="map-actions">
              <button 
                className="btn primary confirm-location-btn"
                onClick={handleConfirmLocation}
                disabled={!selectedLocation}
              >
                Confirm location & proceed →
              </button>
            </div>
          </div>

          <div className="form-section">
            <div className="form-header">
              <h2>Enter complete address</h2>
            </div>
            <form onSubmit={handleSubmit} className="address-form">
              <div className="address-type-section">
                <label>Save address as *</label>
                <div className="address-type-buttons">
                  {['home', 'work', 'hotel', 'other'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`address-type-btn ${formData.type === type ? 'active' : ''}`}
                      onClick={() => handleTypeChange(type)}
                    >
                      <span className="type-icon">{getAddressTypeIcon(type)}</span>
                      <span className="type-label">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Flat / House no / Building name *</label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter house/flat number"
                />
              </div>

              <div className="form-group">
                <label>Floor (optional)</label>
                <input
                  type="text"
                  name="floor"
                  value={formData.floor}
                  onChange={handleInputChange}
                  placeholder="Enter floor number"
                />
              </div>

              <div className="form-group">
                <label>Area / Sector / Locality *</label>
                <input
                  type="text"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter area/locality"
                />
                {!formData.city && formData.area && (
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    City will be auto-filled from map location
                  </small>
                )}
              </div>

              {formData.city && (
                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter city"
                    />
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter state"
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Pincode *</label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter pincode (auto-filled from map)"
                  pattern="[0-9]{6}"
                  maxLength="6"
                />
                {formData.pincode && (
                  <small style={{ color: '#27ae60', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                    ✓ Pincode fetched from map location
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Nearby landmark (optional)</label>
                <input
                  type="text"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Enter nearby landmark"
                />
              </div>

              <div className="form-divider">
                <p>Enter your details for seamless delivery experience</p>
              </div>

              <div className="form-group">
                <label>Your name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your name"
                />
              </div>

              <div className="form-group">
                <label>Your phone number (optional)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  pattern="[0-9]{10}"
                  maxLength="10"
                />
              </div>

              <button type="submit" className="btn primary save-address-btn">
                Save Address
              </button>
            </form>
          </div>
        </div>

        {/* Mobile Layout: Step by step */}
        <div className="address-form-mobile">
          {step === 'map' && (
            <div className="mobile-map-step">
              <div className="mobile-map-header">
                <button className="back-btn" onClick={onClose}>←</button>
                <h2>Confirm map pin location</h2>
              </div>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={formData.coordinates || { lat: 26.7588, lng: 83.3697 }}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                pincode={formData.pincode}
              />
              <div className="mobile-map-actions">
                <button 
                  className="btn primary confirm-location-btn"
                  onClick={handleConfirmLocation}
                  disabled={!selectedLocation}
                >
                  Confirm location & proceed →
                </button>
              </div>
            </div>
          )}

          {step === 'form' && (
            <div className="mobile-form-step">
              <div className="mobile-form-header">
                <button className="back-btn" onClick={() => setStep('map')}>←</button>
                <h2>Enter complete address</h2>
                <button className="close-btn" onClick={onClose}>×</button>
              </div>
              <form onSubmit={handleSubmit} className="address-form">
                <div className="address-type-section">
                  <label>Save address as *</label>
                  <div className="address-type-buttons">
                    {['home', 'work', 'hotel', 'other'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`address-type-btn ${formData.type === type ? 'active' : ''}`}
                        onClick={() => handleTypeChange(type)}
                      >
                        <span className="type-icon">{getAddressTypeIcon(type)}</span>
                        <span className="type-label">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Flat / House no / Building name *</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter house/flat number"
                  />
                </div>

                <div className="form-group">
                  <label>Floor (optional)</label>
                  <input
                    type="text"
                    name="floor"
                    value={formData.floor}
                    onChange={handleInputChange}
                    placeholder="Enter floor number"
                  />
                </div>

                <div className="form-group">
                  <label>Area / Sector / Locality *</label>
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter area/locality (auto-filled from map)"
                  />
                  {formData.area && (
                    <small style={{ color: '#27ae60', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                      ✓ Area fetched from map location
                    </small>
                  )}
                </div>

                {formData.city && formData.state && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter city"
                      />
                      {formData.city && (
                        <small style={{ color: '#27ae60', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                          ✓ City fetched from map
                        </small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter state"
                      />
                      {formData.state && (
                        <small style={{ color: '#27ae60', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                          ✓ State fetched from map
                        </small>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter pincode (auto-filled from map)"
                    pattern="[0-9]{6}"
                    maxLength="6"
                  />
                  {formData.pincode && (
                    <small style={{ color: '#27ae60', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                      ✓ Pincode fetched from map location
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Nearby landmark (optional)</label>
                  <input
                    type="text"
                    name="landmark"
                    value={formData.landmark}
                    onChange={handleInputChange}
                    placeholder="Enter nearby landmark"
                  />
                </div>

                <div className="form-divider">
                  <p>Enter your details for seamless delivery experience</p>
                </div>

                <div className="form-group">
                  <label>Your name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your name"
                  />
                </div>

                <div className="form-group">
                  <label>Your phone number (optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    pattern="[0-9]{10}"
                    maxLength="10"
                  />
                </div>

                <button type="submit" className="btn primary save-address-btn">
                  Save Address
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

